
import { ClientFilterObject, WhereFilter } from '@common/filter.decorator';
import { FilterPage, FilterResult } from 'src/app/filter/filter.page';
import { FormFieldValue } from 'src/app/@shared/form/form.types';
import { FormKey } from 'src/app/@shared/form/form-config';
import { FormMainType } from '@lib/types';
import { Injectable } from '@angular/core';
import { MetaDataStore } from 'src/app/@core/content.service';
import { ModalOptions } from '@ionic/angular';
import { OrganizationSubType, OrganizationSubTypeForm } from '@api/organization/@shared/organization.entity';
import { Params } from '@angular/router';

import { BaseListPage } from 'src/app/@shared/base-list-page';
import { LOCATION_SEARCH_KEY, MULTI_FIELD_SEARCH_KEY } from '@common/search-filter.constants';

export const CONTAIN_FILTER_SEPARATOR = '|';

@Injectable()
export abstract class BaseListFilterPage<T extends { id: string }, U> extends BaseListPage<T, U> {
  public filterIsActive: boolean; // where filter excluding search
  public currentSearchValue: string = '';

  public onSearchValueUpdated(event: Event): void {
    const { value } = event.target as HTMLInputElement;

    // Prevent mupltiple requests for the same value (e.g. keyup events)
    if (value === this.currentSearchValue) {
      return;
    }

    this.currentSearchValue = value;

    if (this.content) {
      this.content.scrollToTop().catch();
    } else {
      console.error('onSearchValueUpdated: this.content is not defined for', this.currentContentId);
    }

    this.updateQueryParamsBySearchValue(value, MULTI_FIELD_SEARCH_KEY);

    if (this.isModal) {
      this.onQueryParamsUpdated(this.currentContentId);
    }
  }

  protected async openFilter(
    filterKeys: (keyof U)[],
    formType: FormMainType,
    listSubId?: OrganizationSubType | 'all',
    listSubTypeForm?: OrganizationSubTypeForm,
    formId?: FormKey,
  ): Promise<void> {
    const options: ModalOptions = {
      canDismiss: false,
      component: FilterPage<U>,
      componentProps: {
        appliedWhereFilter: this.filters[this.currentContentId]?.where || {},
        formId,
        formType,
        listSubId,
        listSubTypeForm,
        presentingElement: this.content,
      },
      cssClass: 'proto-modal',
    };

    const { data, role } = await this.presentModal(options);

    if (role === 'cancel') {
      return Promise.resolve();
    }

    this.onFilterUpdated(this.currentContentId, data as FilterResult<U>, filterKeys);

    return Promise.resolve();
  }

  // returns true when request has been made
  protected applyFilterSettings(id: string,
    filter: ClientFilterObject<U>, meta: Partial<MetaDataStore<U>>, indicatorFilterKeys: string[]): boolean {
    const whereFilterByState = (meta.appliedFilter?.where || {}) as WhereFilter<U>;
    const filterByStateInitialized = !!Object.keys(meta).length; // TODO: naming

    const skipRequest = !this.filterHasChanged(filter.where as WhereFilter<U>, whereFilterByState)
      || !filterByStateInitialized;

    this.setFilter(id, {
      ...filter,
    });

    this.currentSearchValue = this.getNormalizedSearchValue(this.queryParams[MULTI_FIELD_SEARCH_KEY]);
    this.filterIsActive = this.hasAppliedFilterSettings(filter.where as WhereFilter<U>, indicatorFilterKeys);

    if (!skipRequest) {
      this.refresh(id).catch();
    }

    return !skipRequest;
  }

  // TODO: this currently only checks where filter
  // if we also want to track properties like sort order, this has to be refactored
  protected filterHasChanged(a: WhereFilter<U>, b: WhereFilter<U>): boolean {
    return JSON.stringify(a) !== JSON.stringify(b);
  }

  protected hasAppliedFilterSettings(where: WhereFilter<U>, filterKeys: string[]): boolean {
    return !!Object.keys(where)
      .filter((key) => { return filterKeys.includes(key); }).length;
  }

  protected getQueryParamArray(key: keyof U | string): string[] {
    const value = this.queryParams[key as string];

    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.map((item) => {
        return item.toString();
      });
    }

    return value.toString()
      .split(CONTAIN_FILTER_SEPARATOR)
      .filter(Boolean);
  }

  protected combineOrBranchGroups(groups: WhereFilter<U>[][]): WhereFilter<U>[] {
    const nonEmptyGroups = groups.filter((group) => {
      return group?.length;
    });

    if (!nonEmptyGroups.length) {
      return [];
    }

    return nonEmptyGroups.reduce((combined, group) => {
      return combined.flatMap((leftBranch) => {
        return group.map((rightBranch) => {
          return {
            ...leftBranch,
            ...rightBranch,
          };
        });
      });
    }, [{} as WhereFilter<U>]);
  }

  protected getWhereFilterByQueryParams(filterKeys: string[]): WhereFilter<U> {
    const where: WhereFilter<U> = {};

    if (!Object.keys(this.queryParams).length) {
      return where;
    }

    for (const param of Object.keys(this.queryParams)) {
      const value = this.queryParams[param];

      if ((value !== null && typeof value !== 'undefined') && filterKeys.includes(param)) {
        where[param] = value;
      }
    }

    return where;
  }

  protected getNormalizedSearchValue(value: unknown): string {
    const normalizedValue = typeof value === 'string'
      ? value.trim()
      : '';

    return normalizedValue;
  }

  protected getQueryParamValue(value: any): FormFieldValue | null {
    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (!value || (Array.isArray(value) && !value.length)) {
      return null;
    }

    if (Array.isArray(value)) {
      return value.join(CONTAIN_FILTER_SEPARATOR);
    }

    return value;
  }

  protected onFilterUpdated(id: string, data: FilterResult<U>, filterKeys: (keyof U)[]): void {
    const where = {} as WhereFilter<U>;

    for (const key of filterKeys) {
      where[key as string] = this.getQueryParamValue(data[key]);
    }

    if (this.content) {
      this.content.scrollToTop().catch();
    } else {
      console.error('onFilterUpdated: this.content is not defined for', this.currentContentId);
    }

    this.updateQueryParamsByWhereFilter(where, filterKeys);

    if (this.isModal) {
      this.onQueryParamsUpdated(id);
    }
  }

  protected updateQueryParamsByWhereFilter(where: WhereFilter<U>, filterKeys: (keyof U)[]): void {
    for (const k of filterKeys) {
      const key = k as string;
      const value = where[key];

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        this.queryParams[key] = value;
      } else {
        delete this.queryParams[key];
      }
    }

    if (!this.isModal) {
      this.updateRouteByQueryParams(this.queryParams).catch();
    }
  }

  protected updateQueryParamsBySearchValue(searchValue: string, key: string = LOCATION_SEARCH_KEY): void {
    if (searchValue) {
      this.queryParams[key] = searchValue;
    } else {
      delete this.queryParams[key];
    }

    if (!this.isModal) {
      this.updateRouteByQueryParams(this.queryParams).catch();
    }
  }

  protected onQueryParamsUpdated(currentContentId: string): void {
    console.error('missing onQueryParamsUpdated call in list page', currentContentId);
  }

  private updateRouteByQueryParams(queryParams: Params): Promise<boolean> {
    // using the absolute path here for split pane layouts
    // which makes it easier to handle scenarios where our edit page is open while changing filter params
    // (e.g. we want to stay on /main/list/detail/edit page when updating search value on list page)
    const [route] = this.router.url.split('?');

    return this.router.navigate([route], {
      queryParams,
    });
  }
}
